import { useState, type FormEvent } from "react";
import { StatusMessage, type Message } from "@/components/StatusMessage";
import { useI18n } from "@/i18n/useI18n";
import type { AuthSession } from "./useAuthSession";

/** Full-screen sign-in / set-password form for the individual auth mode. */
export function AuthGate({ auth }: { auth: AuthSession }) {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<Message | null>(null);
  const shownMessage: Message | null =
    message ??
    (auth.state.status === "invalidRole" ? { kind: "error", text: t("authRoleInvalid") } : null);
  const isSetPassword = auth.state.status === "setPassword";

  const submitLogin = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    const ok = await auth.signIn(email.trim(), password);
    setBusy(false);
    if (!ok) setMessage({ kind: "error", text: t("authErrorGeneric") });
  };

  const forgotPassword = async () => {
    if (!email.trim()) {
      setMessage({ kind: "error", text: t("authResetNeedsEmail") });
      return;
    }
    await auth.requestPasswordReset(email.trim());
    setMessage({ kind: "success", text: t("authResetSent") });
  };

  const submitNewPassword = async (event: FormEvent) => {
    event.preventDefault();
    if (newPassword !== confirmPassword) {
      setMessage({ kind: "error", text: t("authPasswordMismatch") });
      return;
    }
    setBusy(true);
    const ok = await auth.setPassword(newPassword);
    setBusy(false);
    if (!ok) setMessage({ kind: "error", text: t("authSetPasswordError") });
  };

  return (
    <div className="auth-gate">
      <div className="auth-gate-box">
        <div className="auth-gate-icon" aria-hidden="true">
          🔐
        </div>
        <h1>{t("authGateTitle")}</h1>
        <p>{isSetPassword ? t("authSetPasswordSubtitle") : t("authGateSubtitle")}</p>
        {isSetPassword ? (
          <form onSubmit={submitNewPassword} autoComplete="on">
            <div className="auth-gate-field">
              <label htmlFor="auth-new-password">{t("authNewPasswordLabel")}</label>
              <input
                type="password"
                id="auth-new-password"
                autoComplete="new-password"
                minLength={8}
                required
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
              />
            </div>
            <div className="auth-gate-field">
              <label htmlFor="auth-confirm-password">{t("authConfirmPasswordLabel")}</label>
              <input
                type="password"
                id="auth-confirm-password"
                autoComplete="new-password"
                minLength={8}
                required
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
              />
            </div>
            <button type="submit" className="auth-gate-submit" disabled={busy}>
              {t("authSetPasswordButton")}
            </button>
          </form>
        ) : (
          <>
            <form onSubmit={submitLogin} autoComplete="on">
              <div className="auth-gate-field">
                <label htmlFor="auth-email">{t("authEmailLabel")}</label>
                <input
                  type="email"
                  id="auth-email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </div>
              <div className="auth-gate-field">
                <label htmlFor="auth-password">{t("authPasswordLabel")}</label>
                <input
                  type="password"
                  id="auth-password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </div>
              <button type="submit" className="auth-gate-submit" disabled={busy}>
                {t("authLoginButton")}
              </button>
            </form>
            <button type="button" className="auth-link-btn" onClick={forgotPassword}>
              {t("authForgotButton")}
            </button>
          </>
        )}
        <StatusMessage message={shownMessage} />
      </div>
    </div>
  );
}
